import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEstadoApp } from '../context/EstadoAppContext.jsx';
import { useLocalStorage } from '../hooks/useLocalStorage.js';
import { useAlertasMejoramientos } from '../hooks/useAlertasMejoramientos.js';
import { useFormContext } from '../context/FormContext';
import { EVENTO_ACCION_ALERTA } from '../utils/alertasMejoramientos';
import { Modal } from './ui/Modal.jsx';

// Burbuja flotante de alertas del Área de Mejoramientos (migración 076).
//
// Se muestra para los roles que pueden recibir/resolver ('mejoramientos',
// 'admin_mejoramientos'): al loguear recupera las vencias pendientes (igual que
// las notificaciones) y las lista con un panel.
//
// FLUJO (decidido 2026-10-23): al vencer la ventana se envía la NOTIFICACIÓN (mail
// real, una sola vez por ciclo, con texto editable en un modal). La notificación
// NO cierra el ciclo. Después la organización pide la prórroga y recién cuando
// llega la PV se cierra la ventana y se abre el ciclo N+1. La descripción del
// intercambio es opcional y se registra en "Seguimiento de Intercambios" (formC)
// según la modalidad de la obra.
const AlertasMejoramientosBurbuja = () => {
    const [abierto, setAbierto] = useState(false);
    const [cargandoId, setCargandoId] = useState(null);
    const [nroPvPorId, setNroPvPorId] = useState({});
    const [descripcionPorId, setDescripcionPorId] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const { pendientes, cargando, puede, resolver, recargar, prepararNotificacion, prepararEmailRespuesta, enviarNotificacion } = useAlertasMejoramientos();
    const { formA, formC, loadField } = useFormContext();

    // Ref con el estado más reciente del form: la respuesta del backend puede
    // llegar después de que el usuario haya seguido editando la obra, y el
    // closure del handler tendría una versión vieja (podría pisar filas).
    const formARef = useRef(formA);
    const formCRef = useRef(formC);
    useEffect(() => {
        formARef.current = formA;
        formCRef.current = formC;
    }, [formA, formC]);

    // Acción pedida desde "Seguimiento de Intercambios" (tabla del formC).
    const [respuestaAlerta, setRespuestaAlerta] = useState(null);
    const [respuestaForm, setRespuestaForm] = useState({ nroPv: '', plazo: '', descripcion: '' });
    const [respuestaDraft, setRespuestaDraft] = useState(null);
    const [cargandoRespuesta, setCargandoRespuesta] = useState(false);
    const [enviandoRespuesta, setEnviandoRespuesta] = useState(false);

    // Al resolver, el backend inserta la fila del intercambio. Si la obra que
    // está abierta en este cliente es la misma, se agrega al formC para que
    // "Seguimiento de Intercambios" la muestre sin recargar la página.
    const incorporarIntercambio = (intercambio, codigoObra) => {
        if (!intercambio || !codigoObra) return;
        const formAActual = formARef.current;
        const formCActual = formCRef.current;
        if (formAActual?.datosGenerales?.id_partido_obra !== codigoObra) return;
        const etapaII = String(intercambio.etapa || '').trim().toLowerCase() === 'etapa ii'
            || String(intercambio.tabla || '').trim().toLowerCase().endsWith('etapa_ii');
        const campo = etapaII ? 'arrayMejorasEtapaII' : 'arrayMejorasEtapaI';
        const actuales = Array.isArray(formCActual?.[campo]) ? formCActual[campo] : [];
        if (intercambio.id != null && actuales.some((r) => Number(r?.id) === Number(intercambio.id))) {
            return;
        }
        const fila = {
            id: intercambio.id ?? null,
            fecha: intercambio.fecha || null,
            descripcion: intercambio.descripcion || '',
            nro_pv: intercambio.nroPv || '',
            estado: intercambio.estado || '',
        };
        loadField('formC', campo, null, [...actuales, fila]);
        // Ref al día para que dos incorporaciones seguidas (notificación y
        // respuesta) no se pisen antes del próximo render.
        formCRef.current = { ...(formCActual || {}), [campo]: [...actuales, fila] };
    };

    // Modal de notificación: se abre con el borrador que devuelve el backend para
    // esa alerta (destinatarios sugeridos, asunto y cuerpo).
    const [mailAbierto, setMailAbierto] = useState(false);
    const [mailAlerta, setMailAlerta] = useState(null);
    const [mailForm, setMailForm] = useState({ destinatarios: '', asunto: '', cuerpo: '' });
    const [mailModo, setMailModo] = useState('editar');
    const [cargandoMail, setCargandoMail] = useState(false);
    const [enviandoMail, setEnviandoMail] = useState(false);
    // Borradores confirmados del ciclo 1: el modal NO envía, solo edita. Al
    // confirmar se guarda acá y el botón "Enviar notificación" queda habilitado
    // solo si hay al menos un destinatario.
    const [borradorPorId, setBorradorPorId] = useState({});

    const [headerOffset, setHeaderOffset] = useState(84);
    useEffect(() => {
        const medir = () => {
            const h = document.querySelector('header');
            if (h) setHeaderOffset(Math.ceil(h.getBoundingClientRect().height) + 12);
        };
        medir();
        window.addEventListener('resize', medir);
        return () => window.removeEventListener('resize', medir);
    }, []);

    const navigate = useNavigate();
    const { cambiarEstadoApp } = useEstadoApp();
    const { setItem } = useLocalStorage();

    // Buscador por código de obra: filtra lo que muestra el modal. El contador del
    // botón sigue mostrando el total de pendientes (no lo afecta el filtro).
    const termino = busqueda.trim().toLowerCase();
    const visibles = termino
        ? pendientes.filter((a) => (a.codigoObra || '').toLowerCase().includes(termino))
        : pendientes;

    // Doble clic en el código → abrir la obra (patrón de TablaObras/ChatBurbujaGlobal).
    const abrirObra = (codigo) => {
        if (!codigo) return;
        setItem('idPartidoObra', codigo);
        setItem('isEditingPagos', 'false');
        cambiarEstadoApp('edit_desde_pagos', { obra: codigo, origen: 'alertas_mejoramientos' });
        navigate(`/inicio?id_partido_obra=${encodeURIComponent(codigo)}`);
    };

    // Cierra el ciclo con la PV y envía el mail de respuesta en el ciclo 1.
    // Devuelve true si la resolución salió bien. Lo usan el flujo de la burbuja
    // y el modal que se abre desde "Seguimiento de Intercambios".
    const aplicarResolucion = async (alerta, { pv, plazo, descripcion, email }) => {
        if (!alerta || cargandoId !== null) return false;
        const esCiclo1 = Number(alerta.ciclo) === 1;
        setCargandoId(alerta.id);
        const res = await resolver(
            alerta.id,
            pv,
            descripcion || '',
            esCiclo1 ? Number(plazo) : undefined,
            esCiclo1 ? email : undefined
        );
        setCargandoId(null);
        if (!res?.ok) {
            toast.error(res?.error || 'No se pudo resolver la alerta');
            return false;
        }
        incorporarIntercambio(res.data?.intercambio, res.data?.ventana?.codigoObra);
        if (res.data?.nuevaVentana) {
            toast.success(`Alerta resuelta: se abrió el ciclo ${res.data.nuevaVentana.ciclo}.`);
        } else {
            toast.success('Alerta resuelta: la obra está finalizada o en guarda, no se abrió otro ciclo.');
        }
        setNroPvPorId((prev) => ({ ...prev, [alerta.id]: '' }));
        setDescripcionPorId((prev) => ({ ...prev, [alerta.id]: '' }));
        return true;
    };

    // Cierra el ciclo con la PV. El PV es obligatorio: sin providencia no
    // corresponde abrir el ciclo siguiente. El ciclo 1 se resuelve desde el
    // modal combinado (PV + plazo + descripción + mail); este handler queda
    // para los ciclos 2+.
    const handleResolver = async (id) => {
        if (cargandoId !== null) return;
        const pv = (nroPvPorId[id] || '').trim();
        if (!pv) {
            toast.error('Cargá el NRO PV de la Providencia para cerrar el ciclo');
            return;
        }
        const alerta = pendientes.find((p) => p.id === id);
        if (!alerta) {
            toast.error('La alerta ya no está pendiente');
            return;
        }
        await aplicarResolucion(alerta, { pv, descripcion: descripcionPorId[id] || '' });
    };

    const cerrarMail = () => {
        if (enviandoMail) return;
        setMailAbierto(false);
        setMailAlerta(null);
        setMailForm({ destinatarios: '', asunto: '', cuerpo: '' });
    };

    const tieneDestinatario = (valor) =>
        String(valor ?? '')
            .split(/[,;]/)
            .some((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));

    // Abre el modal. En ciclo 1 es un EDITOR (Confirmar no envía); en ciclos 2+
    // mantiene el comportamiento anterior (desde el modal se envía).
    const abrirMail = async (alerta) => {
        const esCiclo1 = Number(alerta.ciclo) === 1;
        setMailAlerta(alerta);
        setMailModo(esCiclo1 ? 'editar' : 'enviar');
        setMailAbierto(true);

        // En ciclo 1, si ya hay un borrador confirmado, se reabre con esos
        // cambios (no se pisan con la plantilla del backend).
        const borrador = esCiclo1 ? borradorPorId[alerta.id] : null;
        if (borrador) {
            setMailForm(borrador);
            setCargandoMail(false);
            return;
        }

        setMailForm({ destinatarios: '', asunto: '', cuerpo: '' });
        setCargandoMail(true);
        const res = await prepararNotificacion(alerta.id);
        setCargandoMail(false);
        if (!res?.ok) {
            setMailAbierto(false);
            setMailAlerta(null);
            toast.error(res?.error || 'No se pudo preparar la notificación');
            return;
        }
        const d = res.data || {};
        // El mail de notificación usa siempre su texto histórico.
        setMailForm({
            destinatarios: Array.isArray(d.destinatarios) ? d.destinatarios.join(', ') : '',
            asunto: d.asunto || '',
            cuerpo: d.cuerpo || '',
        });
    };

    const enviarDesdeModal = async (descripcion = '') => {
        if (enviandoMail || !mailAlerta) return;
        if (!tieneDestinatario(mailForm.destinatarios)) {
            toast.error('Cargá al menos un destinatario válido');
            return;
        }
        setEnviandoMail(true);
        const res = await enviarNotificacion(mailAlerta.id, { ...mailForm, descripcion });
        setEnviandoMail(false);
        if (res?.ok) {
            incorporarIntercambio(res.data?.intercambio, mailAlerta?.codigoObra);
            toast.success(`Mail de notificación enviado a ${(res.data?.destinatarios || []).join(', ')}.`);
            setMailAbierto(false);
            setMailAlerta(null);
            setMailForm({ destinatarios: '', asunto: '', cuerpo: '' });
        } else {
            toast.error(res?.error || 'No se pudo enviar el mail');
        }
    };

    const handleConfirmarModal = async () => {
        if (!mailAlerta) return;
        if (!mailForm.asunto.trim() || !mailForm.cuerpo.trim()) {
            toast.error('El asunto y el cuerpo del mail son obligatorios');
            return;
        }
        if (mailModo === 'enviar') {
            await enviarDesdeModal('');
            return;
        }
        // Ciclo 1: solo guarda el borrador. El envío es un paso aparte en la
        // tarjeta, habilitado únicamente si hay destinatario.
        setBorradorPorId((prev) => ({
            ...prev,
            [mailAlerta.id]: {
                destinatarios: mailForm.destinatarios,
                asunto: mailForm.asunto,
                cuerpo: mailForm.cuerpo,
            },
        }));
        setMailAbierto(false);
        setMailAlerta(null);
        setMailForm({ destinatarios: '', asunto: '', cuerpo: '' });
    };

    // Ciclo 1: envía la notificación ya confirmada. No cierra el ciclo; queda
    // registrada en Seguimiento de Intercambios (sin PV, con descripción opcional).
    const handleEnviarCiclo1 = async (id) => {
        if (enviandoMail) return;
        const borrador = borradorPorId[id];
        if (!borrador) {
            toast.error('Primero editá y confirmá la notificación');
            return;
        }
        if (!tieneDestinatario(borrador.destinatarios)) {
            toast.error('Cargá al menos un destinatario válido');
            return;
        }
        const descripcion = (descripcionPorId[id] || '').trim();
        if (!descripcion) {
            toast.error('En el ciclo 1 la descripción es obligatoria: queda registrada en Seguimiento de Intercambios');
            return;
        }
        setEnviandoMail(true);
        const res = await enviarNotificacion(id, {
            ...borrador,
            descripcion,
        });
        setEnviandoMail(false);
        if (res?.ok) {
            const alerta = pendientes.find((p) => p.id === id);
            incorporarIntercambio(res.data?.intercambio, alerta?.codigoObra);
            toast.success(`Mail de notificación enviado a ${(res.data?.destinatarios || []).join(', ')}.`);
            setBorradorPorId((prev) => {
                const copia = { ...prev };
                delete copia[id];
                return copia;
            });
            setDescripcionPorId((prev) => ({ ...prev, [id]: '' }));
        } else {
            toast.error(res?.error || 'No se pudo enviar el mail');
        }
    };

    // ------------------------------------------------------------------
    // Acciones pedidas desde "Seguimiento de Intercambios" (formC)
    // ------------------------------------------------------------------
    // La tabla muestra un botón "Enviar notificación" cuando hay una ventana
    // vencida sin notificar, y "Respuesta" en la fila Recibido + prórroga.
    // No duplica los modales: pide la acción por evento y la burbuja la ejecuta.

    const abrirRespuestaCiclo1 = (alerta) => {
        if (!alerta) return;
        setRespuestaAlerta(alerta);
        setRespuestaForm({ nroPv: '', plazo: '', descripcion: '' });
        setRespuestaDraft(null);
    };

    const cerrarRespuestaCiclo1 = () => {
        if (enviandoRespuesta) return;
        setRespuestaAlerta(null);
        setRespuestaForm({ nroPv: '', plazo: '', descripcion: '' });
        setRespuestaDraft(null);
    };

    // El borrador del mail depende del plazo (incluye la nueva fecha límite),
    // así que se pide al backend cada vez que cambia el plazo.
    const cargarDraftRespuestaCiclo1 = async (plazo) => {
        if (!respuestaAlerta || !plazo) return;
        setCargandoRespuesta(true);
        const res = await prepararEmailRespuesta(respuestaAlerta.id, Number(plazo));
        setCargandoRespuesta(false);
        if (!res?.ok) {
            setRespuestaDraft(null);
            toast.error(res?.error || 'No se pudo preparar el mail de respuesta');
            return;
        }
        const d = res.data || {};
        setRespuestaDraft({
            plazo: Number(plazo),
            destinatarios: Array.isArray(d.destinatarios) ? d.destinatarios.join(', ') : (d.destinatarios || ''),
            asunto: d.asunto || '',
            cuerpo: d.cuerpo || '',
        });
    };

    const enviarRespuestaCiclo1 = async () => {
        if (!respuestaAlerta || enviandoRespuesta) return;
        const pv = (respuestaForm.nroPv || '').trim();
        if (!pv) {
            toast.error('Cargá el NRO PV de la Providencia');
            return;
        }
        const plazo = Number(respuestaForm.plazo);
        if (![30, 60, 90, 120].includes(plazo)) {
            toast.error('Elegí el plazo otorgado (30, 60, 90 o 120 días)');
            return;
        }
        if (!respuestaDraft || Number(respuestaDraft.plazo) !== plazo) {
            toast.error('Cargá el mail de respuesta para el plazo elegido');
            return;
        }
        if (!String(respuestaDraft.asunto || '').trim() || !String(respuestaDraft.cuerpo || '').trim()) {
            toast.error('El asunto y el cuerpo del mail son obligatorios');
            return;
        }
        if (!tieneDestinatario(respuestaDraft.destinatarios)) {
            toast.error('Cargá al menos un destinatario válido');
            return;
        }
        setEnviandoRespuesta(true);
        const ok = await aplicarResolucion(respuestaAlerta, {
            pv,
            plazo,
            descripcion: respuestaForm.descripcion,
            email: {
                destinatarios: respuestaDraft.destinatarios,
                asunto: respuestaDraft.asunto,
                cuerpo: respuestaDraft.cuerpo,
            },
        });
        setEnviandoRespuesta(false);
        if (ok) {
            setRespuestaAlerta(null);
            setRespuestaForm({ nroPv: '', plazo: '', descripcion: '' });
            setRespuestaDraft(null);
        }
    };

    useEffect(() => {
        const onAccion = async (e) => {
            const detalle = e?.detail || {};
            if (!detalle.codigoObra || !detalle.accion) return;
            setBusqueda(detalle.codigoObra);
            setAbierto(true);
            // Se recarga acá (y no se espera al efecto de `pendientes`) para
            // evitar disparar sobre una lista vieja. `recargar` devuelve la
            // lista nueva además de actualizar el estado.
            const lista = await recargar();
            const ventanas = Array.isArray(lista) ? lista : [];
            const alerta = ventanas.find((p) => (p.codigoObra || '') === detalle.codigoObra);
            if (!alerta) {
                toast.info(`No hay una alerta vencida pendiente para ${detalle.codigoObra}.`);
                return;
            }
            if (detalle.accion === 'notificacion') {
                if (alerta.notificacionEnviadaEn) {
                    toast.info('La notificación de este ciclo ya fue enviada.');
                    return;
                }
                abrirMail(alerta);
                return;
            }
            if (detalle.accion === 'respuesta_ciclo1') {
                if (Number(alerta.ciclo) !== 1) {
                    toast.info('La obra no tiene el ciclo 1 pendiente.');
                    return;
                }
                if (!alerta.notificacionEnviadaEn) {
                    toast.info('Primero enviá la notificación de plazo vencido.');
                    return;
                }
                if (!alerta.prorrogaSolicitada) {
                    toast.info('Guardá la obra con la nota de prórroga (Recibido + Solicitud de prórroga) antes de enviar la respuesta.');
                    return;
                }
                abrirRespuestaCiclo1(alerta);
            }
        };
        window.addEventListener(EVENTO_ACCION_ALERTA, onAccion);
        return () => window.removeEventListener(EVENTO_ACCION_ALERTA, onAccion);
    }, [recargar, abrirMail, abrirRespuestaCiclo1]);


    // El backend devuelve "YYYY-MM-DD". Si alguna vez llega un ISO con hora,
    // tomamos solo el día: interpretar siempre como fecha de calendario, sin
    // correr el offset de TZ (un "…T00:00:00.000Z" en UTC-3 mostraría el día previo).
    const parseFechaDia = (v) => {
        if (!v) return null;
        const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    const fecha = (iso) => {
        const d = parseFechaDia(iso);
        return d ? d.toLocaleDateString('es-AR') : '—';
    };

    // Contadores calculados en el backend con CURRENT_DATE.
    const transcurridos = (n) => (n == null ? '—' : `${n} días`);
    const restantes = (n) => {
        if (n == null) return '—';
        if (n > 0) return `faltan ${n} día${n === 1 ? '' : 's'}`;
        if (n === 0) return 'vence hoy';
        return `vencida hace ${-n} día${-n === 1 ? '' : 's'}`;
    };

    // Sin pendientes no se muestra la burbuja (ni el botón): no hay nada que resolver.
    if (!puede || pendientes.length === 0) return null;

    return (
        <>
            {/* Botón flotante: por debajo del header (se mide su altura real),
                en la esquina superior derecha junto al botón de "Cerrar sesión". */}
            <button
                type="button"
                onClick={() => setAbierto((v) => !v)}
                title="Alertas de Mejoramientos (90 días)"
                style={{ top: headerOffset }}
                className="fixed right-4 z-50 w-14 h-14 rounded-full bg-amber-500 text-white shadow-lg hover:bg-amber-600 flex items-center justify-center"
            >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {pendientes.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                        {pendientes.length}
                    </span>
                )}
            </button>

            {abierto && (
                <div style={{ top: headerOffset + 64 }} className="fixed right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-7rem)] bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden border border-amber-200 dark:border-amber-800">
                    <div className="px-4 py-3 bg-amber-500 text-white">
                        <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm">Alertas de Mejoramientos</p>
                            <button type="button" onClick={() => setAbierto(false)} className="text-white/80 hover:text-white text-lg leading-none">✕</button>
                        </div>
                        <p className="text-xs text-white/80 mt-0.5">Pago al 100% · vencen a los 90 días</p>
                    </div>

                    <div className="px-4 pt-3 bg-gray-50 dark:bg-gray-900">
                        <input
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por código de obra (ej. 025-012)"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-900">
                        {cargando && pendientes.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">Cargando…</div>
                        )}
                        {!cargando && pendientes.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                                Sin alertas vencidas. Te avisamos cuando un pago al 100% cumpla los 90 días.
                            </div>
                        )}
                        {!cargando && pendientes.length > 0 && visibles.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                                Sin coincidencias para «{busqueda.trim()}».
                            </div>
                        )}
                        {visibles.map((a) => {
                            const vencioHace = a.diasRestantes != null && a.diasRestantes < 0 ? -a.diasRestantes : 0;
                            const notificacionEnviada = Boolean(a.notificacionEnviadaEn);
                            const esCiclo1 = Number(a.ciclo) === 1;
                            const borrador = borradorPorId[a.id];
                            return (
                                <div key={a.id} className="rounded-lg border border-amber-200 dark:border-amber-700 p-3 bg-white dark:bg-gray-800 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <button
                                                type="button"
                                                onDoubleClick={() => abrirObra(a.codigoObra)}
                                                title="Doble clic para abrir la obra"
                                                className="font-medium text-blue-700 dark:text-blue-400 hover:underline"
                                            >
                                                {a.codigoObra}
                                            </button>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                                {a.nombreObra}
                                            </p>
                                        </div>
                                        <span className="shrink-0 px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-semibold">
                                            {vencioHace} día{vencioHace !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Ciclo {a.ciclo} · Inicio {fecha(a.fechaBase)} · Vence {fecha(a.fechaVencimiento)}
                                        {a.estadoObra ? ` · ${a.estadoObra}` : ''}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-4">
                                        <span>Transcurridos: <span className="font-semibold">{transcurridos(a.diasTranscurridos)}</span></span>
                                        <span>Restantes: <span className="font-semibold">{restantes(a.diasRestantes)}</span></span>
                                    </div>

                                    {esCiclo1 ? (
                                        <div className="space-y-2">
                                            {notificacionEnviada ? (
                                                <>
                                                    <div className="flex items-start gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 dark:border-green-700 dark:bg-green-900/30">
                                                        <svg className="w-5 h-5 shrink-0 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <div>
                                                            <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                                                                Notificación enviada el {fecha(a.notificacionEnviadaEn)}
                                                            </p>
                                                            <p className="text-xs text-green-700 dark:text-green-400/80">
                                                                El ciclo sigue abierto hasta que se resuelva con la PV.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {a.prorrogaSolicitada ? (
                                                        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                                            <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 dark:border-blue-700 dark:bg-blue-900/30">
                                                                <svg className="w-5 h-5 shrink-0 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                                                                </svg>
                                                                <div>
                                                                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                                                                        Prórroga solicitada el {fecha(a.prorrogaSolicitadaEn)}.
                                                                    </p>
                                                                    <p className="text-xs text-blue-700 dark:text-blue-400/80">
                                                                        Cargá la PV, el plazo y la descripción, y editá el mail en el mismo paso.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => abrirRespuestaCiclo1(a)}
                                                                className="w-full px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                                                            >
                                                                Enviar el mail con la prórroga
                                                            </button>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                Se abre el modal para cargar PV, plazo y descripción, editar el mail y resolver el ciclo 1.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-700 dark:bg-blue-900/20">
                                                            <p className="text-xs text-blue-800 dark:text-blue-300">
                                                                Esperando la nota de la organización con la solicitud de prórroga. Registrala en <strong>Seguimiento de Intercambios</strong> con estado <strong>Recibido</strong> y tildá <strong>Solicitud de prórroga</strong>; recién ahí se habilita el cierre del ciclo 1.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={() => abrirObra(a.codigoObra)}
                                                                className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-900 underline dark:text-blue-300 dark:hover:text-blue-200"
                                                            >
                                                                Abrir la obra para registrar la nota
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                                                        Descripción del intercambio <span className="text-red-500">*</span>
                                                    </label>
                                                    <textarea
                                                        value={descripcionPorId[a.id] || ''}
                                                        onChange={(e) => setDescripcionPorId((prev) => ({ ...prev, [a.id]: e.target.value }))}
                                                        placeholder="Obligatoria en el ciclo 1: queda registrada en Seguimiento de Intercambios"
                                                        rows={2}
                                                        maxLength={2500}
                                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => abrirMail(a)}
                                                        disabled={cargandoId !== null || enviandoMail}
                                                        className="w-full px-3 py-1.5 rounded-md text-sm font-medium border border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Editar notificación
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEnviarCiclo1(a.id)}
                                                        disabled={enviandoMail || !tieneDestinatario(borrador?.destinatarios) || !(descripcionPorId[a.id] || '').trim()}
                                                        title={!borrador
                                                            ? 'Primero editá y confirmá la notificación'
                                                            : (!(descripcionPorId[a.id] || '').trim() ? 'La descripción es obligatoria en el ciclo 1' : undefined)}
                                                        className="w-full px-3 py-1.5 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {enviandoMail ? 'Enviando…' : 'Enviar notificación'}
                                                    </button>
                                                    <p className="text-xs text-gray-400 dark:text-gray-500">
                                                        {borrador
                                                            ? 'Enviar no cierra el ciclo: la descripción y el mail quedan registrados en Seguimiento de Intercambios y el ciclo sigue abierto.'
                                                            : 'Editá y confirmá la notificación, y completá la descripción, para habilitar el envío.'}
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            {/* Ciclos 2+: el modal envía directamente. */}
                                            <div className="space-y-1">
                                                {notificacionEnviada ? (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        Notificación enviada el <span className="font-semibold">{fecha(a.notificacionEnviadaEn)}</span>.
                                                    </p>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => abrirMail(a)}
                                                        disabled={cargandoId !== null}
                                                        className="w-full px-3 py-1.5 rounded-md text-sm font-medium border border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Enviar notificación
                                                    </button>
                                                )}
                                            </div>

                                            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                                <input
                                                    value={nroPvPorId[a.id] || ''}
                                                    onChange={(e) => setNroPvPorId((prev) => ({ ...prev, [a.id]: e.target.value }))}
                                                    placeholder="Nro de Providencia (PV) — obligatorio"
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                />
                                                <textarea
                                                    value={descripcionPorId[a.id] || ''}
                                                    onChange={(e) => setDescripcionPorId((prev) => ({ ...prev, [a.id]: e.target.value }))}
                                                    placeholder="Descripción del intercambio (opcional; se registra en Seguimiento de Intercambios)"
                                                    rows={2}
                                                    maxLength={2500}
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleResolver(a.id)}
                                                    disabled={cargandoId !== null || !(nroPvPorId[a.id] || '').trim()}
                                                    className="w-full px-3 py-1.5 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {cargandoId === a.id ? 'Resolviendo…' : 'Resolver con PV y abrir nuevo ciclo'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <Modal
                isOpen={Boolean(respuestaAlerta)}
                onClose={cerrarRespuestaCiclo1}
                title={`Enviar respuesta de prórroga · ${respuestaAlerta?.codigoObra || ''}`}
                maxWidth="max-w-2xl"
            >
                {respuestaAlerta && (
                    <div className="space-y-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Se envía el mail con el plazo otorgado, se resuelve el ciclo 1 y se abre el ciclo siguiente.
                            La nota con la solicitud de prórroga tiene que estar guardada en Seguimiento de Intercambios.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nro de Providencia (PV) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={respuestaForm.nroPv}
                                    onChange={(e) => setRespuestaForm((prev) => ({ ...prev, nroPv: e.target.value }))}
                                    placeholder="N° PV"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Plazo otorgado <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={respuestaForm.plazo}
                                    onChange={(e) => {
                                        const valor = e.target.value;
                                        setRespuestaForm((prev) => ({ ...prev, plazo: valor }));
                                        setRespuestaDraft(null);
                                        if (valor) cargarDraftRespuestaCiclo1(valor);
                                    }}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                >
                                    <option value="">Elegí el plazo…</option>
                                    {[30, 60, 90, 120].map((p) => (
                                        <option key={p} value={p}>{p} días</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Descripción del intercambio (opcional)
                            </label>
                            <textarea
                                value={respuestaForm.descripcion}
                                onChange={(e) => setRespuestaForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                                rows={2}
                                maxLength={2500}
                                placeholder="Se registra en Seguimiento de Intercambios junto con la PV"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
                            />
                        </div>

                        {cargandoRespuesta && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Cargando el mail de respuesta…</p>
                        )}
                        {!cargandoRespuesta && !respuestaDraft && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Elegí el plazo para cargar el mail de respuesta.
                            </p>
                        )}
                        {respuestaDraft && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Destinatarios
                                    </label>
                                    <input
                                        type="text"
                                        value={respuestaDraft.destinatarios}
                                        onChange={(e) => setRespuestaDraft((prev) => ({ ...prev, destinatarios: e.target.value }))}
                                        placeholder="mail@organizacion.org, otro@organizacion.org"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Se separan con coma. Sin destinatario no se puede enviar.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Asunto
                                    </label>
                                    <input
                                        type="text"
                                        value={respuestaDraft.asunto}
                                        onChange={(e) => setRespuestaDraft((prev) => ({ ...prev, asunto: e.target.value }))}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Cuerpo del mail
                                    </label>
                                    <textarea
                                        value={respuestaDraft.cuerpo}
                                        onChange={(e) => setRespuestaDraft((prev) => ({ ...prev, cuerpo: e.target.value }))}
                                        rows={16}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y font-mono"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Si cambiás el plazo, el borrador se vuelve a cargar y se pierden los cambios de texto.
                                    </p>
                                </div>
                            </>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={cerrarRespuestaCiclo1}
                                disabled={enviandoRespuesta}
                                className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={enviarRespuestaCiclo1}
                                disabled={enviandoRespuesta || cargandoRespuesta || !respuestaDraft}
                                className="px-3 py-1.5 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {enviandoRespuesta ? 'Enviando…' : 'Resolver ciclo 1 y enviar mail'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                isOpen={mailAbierto}
                onClose={cerrarMail}
                title={`${mailModo === 'editar' ? 'Editar notificación' : 'Enviar notificación'}${mailAlerta ? ` · ${mailAlerta.codigoObra}` : ''}`}
                maxWidth="max-w-2xl"
            >
                {cargandoMail ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Cargando el texto del mail…</p>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Destinatarios
                            </label>
                            <input
                                type="text"
                                value={mailForm.destinatarios}
                                onChange={(e) => setMailForm((prev) => ({ ...prev, destinatarios: e.target.value }))}
                                placeholder="mail@organizacion.org, otro@organizacion.org"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Se separan con coma. Sin destinatario no se puede enviar.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Asunto
                            </label>
                            <input
                                type="text"
                                value={mailForm.asunto}
                                onChange={(e) => setMailForm((prev) => ({ ...prev, asunto: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Cuerpo del mail
                            </label>
                            <textarea
                                value={mailForm.cuerpo}
                                onChange={(e) => setMailForm((prev) => ({ ...prev, cuerpo: e.target.value }))}
                                rows={18}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y font-mono"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {mailModo === 'editar'
                                    ? 'Al confirmar NO se envía el mail: se guarda el borrador y volvés a la alerta para enviarlo.'
                                    : 'El texto es un borrador: completá lo que falte antes de enviar. El envío no cierra el ciclo.'}
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={cerrarMail}
                                disabled={enviandoMail}
                                className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmarModal}
                                disabled={enviandoMail}
                                className="px-3 py-1.5 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {mailModo === 'editar'
                                    ? 'Confirmar'
                                    : (enviandoMail ? 'Enviando…' : 'Enviar mail')}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default AlertasMejoramientosBurbuja;
